import type { PrimerjavaUvozov } from '@koda/shared';
import type { JSX } from 'react';

import { decimalno, znesek } from '../lib/format';

const MESECI = ['otvoritev', 'januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'];

const imeMeseca = (m: number): string => MESECI[m] ?? String(m);
const nastej = (meseci: number[]): string => meseci.map(imeMeseca).join(', ');

/**
 * Primerjava novega uvoza s prejšnjim veljavnim uvozom istega leta po kontih
 * in mesecih (docs/ARHITEKTURA.md, 5.5 in 5.10). Služi kontroli, da dva
 * vira (npr. Minimax API in izvoz CSV) dasta iste številke na cent; mesece,
 * ki jih ima samo en uvoz, pove posebej, ne kot razliko.
 */
export function PrimerjavaUvoza({ primerjava: p }: { primerjava: PrimerjavaUvozov }): JSX.Element {
  return (
    <section className="primerjava-uvozov">
      <h3>Primerjava s prejšnjim uvozom</h3>
      <p>
        Prejšnji uvoz{p.prejsnji.ime !== null ? ` (${p.prejsnji.ime})` : ''}: {decimalno(String(p.prejsnji.knjizb), 0)}{' '}
        knjižb, novi: {decimalno(String(p.novi.knjizb), 0)} knjižb.
        {p.skupniMeseci.length > 0 && ` Primerjani meseci: ${nastej(p.skupniMeseci)}.`}
        {p.samoVNovem.length > 0 && ` Samo v novem uvozu: ${nastej(p.samoVNovem)}.`}
        {p.samoVPrejsnjem.length > 0 && ` Samo v prejšnjem uvozu: ${nastej(p.samoVPrejsnjem)}.`}
      </p>
      {p.skupniMeseci.length === 0 ? (
        <p className="drobno">Uvoza nimata skupnih mesecev, zato primerjave saldov ni.</p>
      ) : p.enako ? (
        <p className="uspeh">
          V primerjanih mesecih se salda vseh kontov ujemajo na cent ({decimalno(String(p.ujemanj), 0)} parov konto–mesec).
        </p>
      ) : (
        <>
          <p className="opozorilo">
            V primerjanih mesecih se razlikuje {decimalno(String(p.razlike.length), 0)} parov konto–mesec (
            {decimalno(String(p.ujemanj), 0)} se jih ujema). Saldo je dobro − breme.
          </p>
          <div className="tabela-ovoj">
            <table className="nastavitve razlike-uvozov">
              <thead>
                <tr>
                  <th>Konto</th>
                  <th>Mesec</th>
                  <th className="stevilka">Prejšnji</th>
                  <th className="stevilka">Novi</th>
                  <th className="stevilka">Razlika</th>
                </tr>
              </thead>
              <tbody>
                {p.razlike.map((r) => (
                  <tr key={`${r.konto}-${String(r.mesec)}`}>
                    <td>
                      <code>{r.konto}</code>
                    </td>
                    <td>{imeMeseca(r.mesec)}</td>
                    <td className="stevilka">{znesek(r.prejsnji)}</td>
                    <td className="stevilka">{znesek(r.novi)}</td>
                    <td className="stevilka">{znesek(r.razlika)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
